type OpenReplitParams = {
  replitOrigin: string;
  token: string;
  existingPopup?: Window | null;
};

const TOKEN_RETRY_INTERVAL_MS = 300;
const TOKEN_TIMEOUT_MS = 10000;

function deriveOrigin(input: string): string {
  try {
    return new URL(input).origin;
  } catch (_e) {
    return input;
  }
}

export function openReplitWithToken({
  replitOrigin,
  token,
  existingPopup,
}: OpenReplitParams): Promise<void> {
  return new Promise((resolve, reject) => {
    const strictOrigin = deriveOrigin(replitOrigin);

    let child: Window | null = existingPopup || null;

    if (!child || child.closed) {
      child = window.open(replitOrigin, '_blank');
      if (!child) {
        reject(new Error('Failed to open popup window'));
        return;
      }
    } else {
      child.location.href = replitOrigin;
    }

    let confirmed = false;

    const sendToken = () => {
      if (child && !child.closed) {
        try {
          child.postMessage({ type: 'memrise-token', token }, strictOrigin);
        } catch (e) {
          // Silently fail and retry
        }
      }
    };

    const onMessage = (e: MessageEvent) => {
      if (e.origin !== strictOrigin) return;

      if (e.data?.type === 'replit-ready') {
        sendToken();
      }

      if (e.data?.type === 'replit-token-received') {
        confirmed = true;
        window.removeEventListener('message', onMessage);
        resolve();
      }
    };
    window.addEventListener('message', onMessage);

    const interval = setInterval(() => {
      if (confirmed || !child || child.closed) {
        clearInterval(interval);
      } else {
        sendToken();
      }
    }, TOKEN_RETRY_INTERVAL_MS);

    setTimeout(() => {
      clearInterval(interval);
      window.removeEventListener('message', onMessage);
      resolve();
    }, TOKEN_TIMEOUT_MS);
  });
}

type RegisterParentHandlersParams = {
  replitOrigin: string;
  onWebAuth?: (token: string) => void | Promise<void>;
};

export function registerReplitParentHandlers({
  replitOrigin,
  onWebAuth,
}: RegisterParentHandlersParams): () => void {
  const strictOrigin = deriveOrigin(replitOrigin);
  const handler = (e: MessageEvent) => {
    if (e.origin !== strictOrigin) return;
    const data = e.data;
    if (data?.type === 'memrise-web-auth' && typeof onWebAuth === 'function') {
      const token = data.token;
      if (typeof token === 'string' && token.length > 0) {
        void onWebAuth(token);
      }
    }
  };

  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}
